/**
 * 시즌 설정 (Single Source of Truth)
 *
 * 모든 랜딩페이지/광고/이메일이 이 파일을 참조합니다.
 * 시즌이 바뀌어도 가격/쿠폰/결제 URL은 절대 변경되지 않으며,
 * 표시되는 카피(badge, urgency, deadline)만 자동 전환됩니다.
 *
 * 새로운 시즌을 추가하려면:
 * 1. SEASONS 객체에 시즌 정의 추가
 * 2. SEASON_TIMELINE 배열에 시점 순서대로 등록
 * 3. 끝. (페이지는 자동 반영)
 *
 * ═══════════════════════════════════════════════════════════════
 * ■ Cutover 시간 분기 시스템 (2026-05-27 도입)
 * ═══════════════════════════════════════════════════════════════
 * 현재 cutover: 2026-06-01 00:00 KST (50% → 30% 자동 전환)
 * - 5/31 23:59:59까지: 12mo 99k / 6mo 69k / 3mo 49k, "50% 할인"
 * - 6/1 00:00:00부터: 12mo 140k / 6mo 82.5k / 3mo 51k, "최대 30% 할인"
 * - 백엔드(backend/config/season_config.py CUTOVER_ISO + 
 *         backend/routes/payment.py NEWYEAR2026.cutover_iso)와 1:1 매칭
 *
 * ═══════════════════════════════════════════════════════════════
 * ■ 다음 월별 전환 가이드 (예: 7/1 30% → 20%)
 * ═══════════════════════════════════════════════════════════════
 * 다음 3곳을 모두 같이 갱신하면 자동 적용됨:
 *
 * 1. frontend/src/config/seasonConfig.js (이 파일):
 *    - CUTOVER_ISO 상수 갱신 (예: '2026-07-01T00:00:00+09:00')
 *    - NEWYEAR_PRICES_BEFORE_CUTOVER ← 현재의 _AFTER_CUTOVER 값으로 복사
 *    - NEWYEAR_PRICES_AFTER_CUTOVER ← 새 가격
 *    - DISCOUNT_LABELS_BEFORE/AFTER 갱신
 *    - _applyCutoverToSeason()의 string replace 로직 검토 (50/30이 다른 비율로 바뀌면)
 *
 * 2. backend/config/season_config.py:
 *    - CUTOVER_ISO 갱신
 *    - NEWYEAR_PRICES_BEFORE_CUTOVER / AFTER_CUTOVER 동일 갱신
 *    - NEWYEAR_DISCOUNT_LABELS_BEFORE / AFTER 갱신
 *
 * 3. backend/routes/payment.py NEWYEAR2026 쿠폰:
 *    - cutover_iso 갱신
 *    - fixed_prices_before_cutover ← 현재 신가로 복사
 *    - fixed_prices_after_cutover ← 새 가격
 *    - fixed_prices fallback ← 새 가격
 *
 * 4. 도커 회귀 테스트 (전환 직전·직후 mock 시뮬레이션)
 * 5. 광고 매니저 신규 캐러셀 교체 (마케팅 팀, 코드 외)
 *
 * 자세한 가이드: docs/seasonal-discount-cutover-guide.md
 *
 * ═══════════════════════════════════════════════════════════════
 * 결제 무결성:
 * - promoCode는 모두 'newyear2026' 동일 (백엔드 쿠폰 valid_until 2030-12-31)
 * - 결제 amount는 백엔드가 시점별로 최종 검증 (서버사이드 SSoT)
 * - 프론트 페이지 가격은 참고용 (백엔드 NEWYEAR2026 쿠폰이 진실)
 *
 * 마지막 업데이트: 2026-05-27 (cutover 시간 분기 시스템 도입)
 */

import {
  PRO50_EVENT_END,
  PRO50_EVENT_START,
  PRO50_PRICES,
  isPro50Active,
} from '../utils/pro50Campaign';

const KST = '+09:00';

const buildDate = (iso) => new Date(iso);

// ── Cutover 시간 분기 (2026-06-01 00:00 KST) ────────────────────
export const CUTOVER_ISO = `2026-06-01T00:00:00${KST}`;
const CUTOVER_MS = buildDate(CUTOVER_ISO).getTime();

export const NEWYEAR_PRICES_BEFORE_CUTOVER = {
  '1month': 24000,
  '3month': 49000,    // 18% 할인
  '6month': 69000,    // 37% 할인
  '12month': 99000,   // 50% 할인
};

export const NEWYEAR_PRICES_AFTER_CUTOVER = {
  '1month': 24000,
  '3month': 51000,    // 15% 할인
  '6month': 82500,    // 25% 할인
  '12month': 140000,  // 30% 할인
};

const DISCOUNT_LABELS_BEFORE = {
  '12month': '50%', '6month': '37%', '3month': '18%',
  max: '최대 50% 할인',
};
const DISCOUNT_LABELS_AFTER = {
  '12month': '30%', '6month': '25%', '3month': '15%',
  max: '최대 30% 할인',
};

export const isAfterCutover = (now = new Date()) => now.getTime() >= CUTOVER_MS;

const PRO50_DISCOUNT_LABELS = {
  '12month': '50%', '6month': '40%', '3month': '30%', '1month': '20%',
  max: '최대 50% 할인',
};

// 10월 이벤트 중에는 시즌가 = 이벤트가 (결제 금액은 서버가 같은 규칙으로 확정)
export const getNewYearPrices = (now = new Date()) => {
  if (isPro50Active(now)) return PRO50_PRICES;
  return isAfterCutover(now) ? NEWYEAR_PRICES_AFTER_CUTOVER : NEWYEAR_PRICES_BEFORE_CUTOVER;
};

export const getNewYearPrice = (planKey, now = new Date()) => getNewYearPrices(now)[planKey];

export const getDiscountLabels = (now = new Date()) => {
  if (isPro50Active(now)) return PRO50_DISCOUNT_LABELS;
  return isAfterCutover(now) ? DISCOUNT_LABELS_AFTER : DISCOUNT_LABELS_BEFORE;
};

export const getMaxDiscountLabel = (now = new Date()) => getDiscountLabels(now).max;

export const getDiscountRateLabel = (planKey, now = new Date()) => getDiscountLabels(now)[planKey];

// SEASONS dict의 urgencyText는 5/31 이전 50% 표기로 정의됨.
// _applyCutoverToSeason()이 6/1 이후엔 자동으로 '최대 30% 할인'으로 치환.
// (단일 SSoT = CUTOVER_ISO. SEASONS 직접 수정 금지)
export const SEASONS = {
  MIDTERM_2026: {
    id: 'midterm_2026',
    label: '중간고사 시즌 특가',
    badgeText: '중간고사 한정',
    urgencyText: '4월 30일까지 50% 할인',
    deadlineLabel: '4월 30일까지',
    ctaText: '지금 할인가로 시작하기',
    headlineSuffix: '중간고사 시즌, 지금이 가장 저렴해요',
    seasonPainline: '중간고사 채점, 1주일이면 끝나야 하는 시즌이에요',
    emailIntro: '중간고사 시즌 특별 할인 혜택',
    promoCode: 'newyear2026',
    proPrice12mo: 99000,
    originalPrice12mo: 200000,
    startISO: `2026-03-01T00:00:00${KST}`,
    endISO: `2026-04-30T23:59:59${KST}`,
  },

  GAJEONG_2026: {
    id: 'gajeong_2026',
    label: '가정의 달 응원',
    badgeText: '가정의 달 한정',
    urgencyText: '5월 31일까지 50% 할인',
    deadlineLabel: '5월 31일까지',
    ctaText: '가정의 달 특별가로 시작하기',
    headlineSuffix: '가정의 달, 선생님 응원 특별가',
    seasonPainline: '5월 행사 + 수행평가 + 생기부 누가기록 동시에 몰리는 달이에요',
    emailIntro: '가정의 달 선생님 응원 특별 할인',
    promoCode: 'newyear2026',
    proPrice12mo: 99000,
    originalPrice12mo: 200000,
    startISO: `2026-05-01T00:00:00${KST}`,
    endISO: `2026-05-31T23:59:59${KST}`,
  },

  ASSESSMENT_2026_MAY: {
    id: 'assessment_2026_may',
    label: '생기부 시즌 응원',
    badgeText: '생기부 시즌 응원',
    urgencyText: '6월 30일까지 50% 할인',
    deadlineLabel: '6월 30일까지',
    ctaText: '생기부 미리 시작하기',
    headlineSuffix: '생기부 시즌, 선생님 미리 응원할게요',
    seasonPainline: '7월 생기부 마감까지 D-30. 6월부터 쌓아두면 칼퇴할 수 있어요',
    emailIntro: '생기부 시즌, 선생님 응원할게요',
    promoCode: 'newyear2026',
    proPrice12mo: 99000,
    originalPrice12mo: 200000,
    startISO: `2026-06-01T00:00:00${KST}`,
    endISO: `2026-06-30T23:59:59${KST}`,
  },

  RECORD_BUILDUP_2026: {
    id: 'record_buildup_2026',
    label: '생기부 빌드업 시즌',
    badgeText: '생기부 D-30',
    urgencyText: '7월 5일까지 50% 할인',
    deadlineLabel: '7월 5일까지',
    ctaText: '생기부 미리 시작하기',
    headlineSuffix: '7월 마감 전, 생기부 미리 쌓아두는 시기',
    seasonPainline: '7월 생기부 마감까지 D-30. 미리 쌓아두면 7월에 칼퇴할 수 있어요',
    emailIntro: '생기부 시즌 미리 준비 할인',
    promoCode: 'newyear2026',
    proPrice12mo: 99000,
    originalPrice12mo: 200000,
    startISO: `2026-07-01T00:00:00${KST}`,
    endISO: `2026-07-05T23:59:59${KST}`,
  },

  RECORD_EXPLODE_2026: {
    id: 'record_explode_2026',
    label: '생기부 마감 시즌',
    badgeText: '생기부 마감 임박',
    urgencyText: '7월 15일까지 50% 할인',
    deadlineLabel: '7월 15일까지',
    ctaText: '생기부 오늘 시작하기',
    headlineSuffix: '30명 생기부, 13시간 → 3분',
    seasonPainline: '7월 학기말 생기부 마감 폭발기. 30명 작성을 3분으로',
    emailIntro: '생기부 마감 시즌 응급 할인',
    promoCode: 'newyear2026',
    proPrice12mo: 99000,
    originalPrice12mo: 200000,
    startISO: `2026-07-06T00:00:00${KST}`,
    endISO: `2026-07-20T23:59:59${KST}`,
  },

  SUMMER_2026: {
    id: 'summer_2026',
    label: '여름방학 + 2학기 준비',
    badgeText: '2학기 준비 특가',
    urgencyText: '8월 31일까지 50% 할인',
    deadlineLabel: '8월 31일까지',
    ctaText: '2학기 미리 준비하기',
    headlineSuffix: '여름방학에 2학기 미리 준비하는 시기',
    seasonPainline: '고1·2 과세특 마감 8/31. 방학 중에 미리 준비해야 해요',
    emailIntro: '여름방학 + 2학기 준비 특별 할인',
    promoCode: 'newyear2026',
    proPrice12mo: 99000,
    originalPrice12mo: 200000,
    startISO: `2026-07-21T00:00:00${KST}`,
    endISO: `2026-08-31T23:59:59${KST}`,
  },

  SECOND_SEMESTER_2026: {
    id: 'second_semester_2026',
    label: '2학기 응원 특가',
    badgeText: '2학기 응원',
    urgencyText: '9월 30일까지 50% 할인',
    deadlineLabel: '9월 30일까지',
    ctaText: '2학기 할인가로 시작하기',
    headlineSuffix: '2학기, 선생님 응원 특별가',
    seasonPainline: '2학기 수행평가·과세특이 한꺼번에 몰리는 시기예요',
    emailIntro: '2학기 선생님 응원 특별 할인',
    promoCode: 'newyear2026',
    proPrice12mo: 99000,
    originalPrice12mo: 200000,
    startISO: `2026-09-01T00:00:00${KST}`,
    endISO: `2026-09-30T23:59:59${KST}`,
  },

  // 10월 PRO 최대 50% 이벤트 (utils/pro50Campaign.js). 이벤트가 그대로라 cutover 치환 대상이 아니다.
  OCTOBER_PRO50_2026: {
    id: 'october_pro50_2026',
    label: '10월 PRO 최대 50%',
    badgeText: '10월 한정',
    urgencyText: '10월 31일까지 최대 50% 할인',
    deadlineLabel: '10월 31일까지',
    ctaText: '10월 할인가로 시작하기',
    headlineSuffix: '10월 한 달, PRO 1년이 반값이에요',
    seasonPainline: '2학기 과세특·기말고사 준비가 몰리는 달이에요',
    emailIntro: '10월 한정 PRO 최대 50% 할인',
    promoCode: 'newyear2026',
    proPrice12mo: 100000,
    originalPrice12mo: 200000,
    startISO: PRO50_EVENT_START,
    endISO: PRO50_EVENT_END,
    isPro50: true,
  },

  SEMESTER_END_2026: {
    id: 'semester_end_2026',
    label: '2학기 마무리 응원',
    badgeText: '2학기 마무리',
    urgencyText: '12월 31일까지 50% 할인',
    deadlineLabel: '12월 31일까지',
    ctaText: '할인가로 시작하기',
    headlineSuffix: '학기말, 선생님 응원 특별가',
    seasonPainline: '학기말 생기부·성적 처리가 몰리는 시기예요',
    emailIntro: '2학기 마무리 선생님 응원 할인',
    promoCode: 'newyear2026',
    proPrice12mo: 99000,
    originalPrice12mo: 200000,
    startISO: `2026-11-01T00:00:00${KST}`,
    endISO: `2026-12-31T23:59:59${KST}`,
  },
};

// 시즌 dict에 cutover 적용 (urgencyText, proPrice12mo 자동 치환)
// 원본 객체는 변경하지 않고 얕은 복사 반환.
const _applyCutoverToSeason = (season, now = new Date()) => {
  if (!season) return season;
  if (season.isPro50) return { ...season };
  const after = isAfterCutover(now);
  const price12mo = after ? 140000 : 99000;

  // urgencyText 자동 치환: '50% 할인' ↔ '최대 30% 할인'
  let urgencyText = season.urgencyText;
  if (after) {
    urgencyText = urgencyText.replace('50% 할인', '최대 30% 할인').replace('최대 최대', '최대');
  } else {
    urgencyText = urgencyText.replace('최대 30% 할인', '50% 할인').replace('30% 할인', '50% 할인');
  }

  return {
    ...season,
    urgencyText,
    proPrice12mo: price12mo,
  };
};

const SEASON_TIMELINE = [
  SEASONS.MIDTERM_2026,
  SEASONS.GAJEONG_2026,
  SEASONS.ASSESSMENT_2026_MAY,
  SEASONS.RECORD_BUILDUP_2026,
  SEASONS.RECORD_EXPLODE_2026,
  SEASONS.SUMMER_2026,
  SEASONS.SECOND_SEMESTER_2026,
  SEASONS.OCTOBER_PRO50_2026,
  SEASONS.SEMESTER_END_2026,
];

const FALLBACK_SEASON = SEASONS.SEMESTER_END_2026;

export const getCurrentSeason = (now = new Date()) => {
  // 10월 이벤트는 시각 + 긴급 스위치(PRO50_EVENT_ENABLED)를 함께 따른다.
  if (isPro50Active(now)) return _applyCutoverToSeason(SEASONS.OCTOBER_PRO50_2026, now);
  for (const season of SEASON_TIMELINE) {
    if (season.isPro50) continue;
    const start = buildDate(season.startISO);
    const end = buildDate(season.endISO);
    if (now >= start && now <= end) {
      return _applyCutoverToSeason(season, now);
    }
  }
  return _applyCutoverToSeason(FALLBACK_SEASON, now);
};

export const getSeasonById = (id, now = new Date()) => {
  if (!id) return null;
  const season = Object.values(SEASONS).find((s) => s.id === id);
  return season ? _applyCutoverToSeason(season, now) : null;
};

export const getDeadlineMs = (season = getCurrentSeason()) => {
  return buildDate(season.endISO).getTime();
};

export const getDeadlineDate = (season = getCurrentSeason()) => {
  return buildDate(season.endISO);
};

/**
 * angle 파라미터 → 시즌 객체 매핑.
 *
 * 2단 마감 구조 (5/31, 6/30) + 7월 생기부 폭발기:
 * - gajeong: 5/31까지는 GAJEONG_2026, 그 이후엔 6월 시즌으로 자동 전환
 * - assessment: 항상 6월 시즌 (ASSESSMENT_2026_MAY = 수행평가 + 생기부 빌드업)
 * - record: 6/30까지는 6월 시즌(생기부 빌드업 메시지 포함), 7/1~7/5는 RECORD_BUILDUP_2026,
 *           7/6 이후엔 RECORD_EXPLODE_2026
 * - angle 없음: getCurrentSeason() 결과 (timeline 순서 매칭)
 *
 * 5~6월 K/R/P 광고세트는 학습 누적을 위해 통운영되며, 페이지의 카운트다운만
 * 시즌별로 자동 전환되어 D-7 폭발기를 5/31, 6/30에 두 번 활용한다.
 */
export const getSeasonForAngle = (angle, now = new Date()) => {
  const gajeongEnd = buildDate(SEASONS.GAJEONG_2026.endISO);
  const junePeakEnd = buildDate(SEASONS.ASSESSMENT_2026_MAY.endISO);
  const recordExplodeStart = buildDate(SEASONS.RECORD_EXPLODE_2026.startISO);
  const recordExplodeEnd = buildDate(SEASONS.RECORD_EXPLODE_2026.endISO);

  // 7월 생기부 시즌이 끝난 뒤(또는 10월 이벤트 중)에는 angle 과 무관하게 현재 시즌을 쓴다.
  if (isPro50Active(now) || now > recordExplodeEnd) return getCurrentSeason(now);

  let season;
  if (angle === 'gajeong') {
    if (now <= gajeongEnd) season = SEASONS.GAJEONG_2026;
    else if (now <= junePeakEnd) season = SEASONS.ASSESSMENT_2026_MAY;
    else if (now >= recordExplodeStart) season = SEASONS.RECORD_EXPLODE_2026;
    else season = SEASONS.RECORD_BUILDUP_2026;
  } else if (angle === 'assessment') {
    if (now <= junePeakEnd) season = SEASONS.ASSESSMENT_2026_MAY;
    else if (now >= recordExplodeStart) season = SEASONS.RECORD_EXPLODE_2026;
    else season = SEASONS.RECORD_BUILDUP_2026;
  } else if (angle === 'record') {
    if (now <= junePeakEnd) season = SEASONS.ASSESSMENT_2026_MAY;
    else if (now >= recordExplodeStart) season = SEASONS.RECORD_EXPLODE_2026;
    else season = SEASONS.RECORD_BUILDUP_2026;
  } else {
    return getCurrentSeason(now);
  }

  return _applyCutoverToSeason(season, now);
};

export const isSeasonExpired = (season, now = new Date()) => {
  return now > buildDate(season.endISO);
};

export const getSeasonProgress = (season = getCurrentSeason(), now = new Date()) => {
  const start = buildDate(season.startISO).getTime();
  const end = buildDate(season.endISO).getTime();
  const total = end - start;
  if (total <= 0) return 1;
  const elapsed = now.getTime() - start;
  return Math.max(0, Math.min(1, elapsed / total));
};

export const SEASON_TIMELINE_ORDERED = SEASON_TIMELINE;

const SeasonConfig = {
  SEASONS,
  getCurrentSeason,
  getSeasonById,
  getDeadlineMs,
  getDeadlineDate,
  getSeasonForAngle,
  isSeasonExpired,
  getSeasonProgress,
  SEASON_TIMELINE_ORDERED,
  // Cutover 헬퍼
  CUTOVER_ISO,
  isAfterCutover,
  getNewYearPrices,
  getNewYearPrice,
  getDiscountLabels,
  getMaxDiscountLabel,
  getDiscountRateLabel,
};

export default SeasonConfig;
